/** Committee roster + pending 3-OTP proposals inbox.
 *
 *  Complements CommitteeInvitePanel (dual-OTP flow for new members).
 *  This surfaces the *seated* committee and the 3-of-N proposal inbox
 *  used to add/remove members AFTER quorum is met.
 */
import { useEffect, useState } from "react";
import { Vote, Plus, KeyRound, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function CommitteeGovernancePanel({ org, onChange, accent = "#F5B82E" }) {
  const [proposals, setProposals] = useState([]);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [approveProp, setApproveProp] = useState(null);
  const [dialogKey, setDialogKey] = useState(0);

  const refresh = async () => {
    if (!org?.id) return;
    try {
      const { data } = await api.get(`/orgs/${org.id}/committee/proposals`);
      setProposals(data.items || []);
    } catch (err) {
      console.warn("[CommitteeGovernancePanel] proposals load skipped:", err?.message);
    }
  };
  useEffect(() => { void refresh(); }, [org?.id]);

  const handleReloadUp = async () => { await refresh(); onChange?.(); };
  const committee = org.committee_members || [];
  const pending = proposals.filter((p) => p.status === "pending");

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Seated committee roster */}
      <div className="cs-card rounded-2xl p-6" data-testid="committee-roster-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Committee</div>
            <h2 className="font-display text-xl uppercase tracking-tight">
              {committee.length} / {org.committee_size || 11} seated
            </h2>
          </div>
          <Dialog open={proposeOpen} onOpenChange={(v) => {
            setProposeOpen(v);
            if (!v) setDialogKey((k) => k + 1);
          }}>
            <DialogTrigger asChild>
              <Button className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/10 hover:bg-white/15 text-white"
                      data-testid="propose-committee-btn">
                <Plus size={12} className="mr-1.5" /> Propose
              </Button>
            </DialogTrigger>
            <ProposeDialog key={dialogKey} onOpenChange={setProposeOpen}
                           orgId={org.id} onCreated={handleReloadUp} />
          </Dialog>
        </div>
        {committee.length === 0 ? (
          <div className="text-sm text-white/55 py-3">
            No committee members yet. Use &ldquo;Invite Member&rdquo; above (dual OTP) or &ldquo;Propose&rdquo; here (3-OTP approval).
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="committee-list">
            {committee.map((m) => (
              <div key={m.id}
                   className="flex items-center gap-3 p-2 rounded-lg bg-white/5 border border-white/10">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold"
                     style={{ background: `${accent}25`, color: accent }}>
                  {m.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{m.name}</div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-white/55">
                    {(m.role || "").replace(/_/g, " ")}
                    {m.active === false && <span className="ml-1 text-amber-300">· pending</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending proposals inbox */}
      <div className="cs-card rounded-2xl p-6" data-testid="proposals-card">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Pending Proposals</div>
        <h2 className="font-display text-xl uppercase tracking-tight mt-1">
          {pending.length} awaiting approvals
        </h2>
        {pending.length === 0 ? (
          <div className="mt-3 text-sm text-white/55">
            All quiet. Proposals show up here when someone wants to add or remove a committee member.
          </div>
        ) : (
          <div className="mt-4 space-y-2.5" data-testid="proposals-list">
            {pending.map((p) => (
              <div key={p.id}
                   className="p-3 rounded-lg bg-amber-500/8 border border-amber-500/25"
                   data-testid={`proposal-${p.id}`}>
                <div className="flex items-center gap-2">
                  <Vote size={12} className="text-amber-300" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300">{p.action}</span>
                  <span className="text-white/40 text-[10px]">·</span>
                  <span className="text-xs text-white/85 font-semibold">{p.member_name}</span>
                </div>
                <div className="mt-1 text-[11px] text-white/55">
                  {p.approvals?.length || 0} / {p.required_approvals} approvals · proposed by {p.proposer_name}
                </div>
                <Button onClick={() => setApproveProp(p)}
                        className="mt-3 h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] bg-amber-500/25 hover:bg-amber-500/35 text-amber-200"
                        data-testid={`approve-btn-${p.id}`}>
                  <KeyRound size={11} className="mr-1.5" /> Submit OTP
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {approveProp && (
        <ApproveDialog prop={approveProp} orgId={org.id}
                       onOpenChange={() => setApproveProp(null)}
                       onApproved={handleReloadUp} />
      )}
    </div>
  );
}

function ProposeDialog({ onOpenChange, orgId, onCreated }) {
  const [action, setAction] = useState("add");
  const [memberName, setMemberName] = useState("");
  const [memberRole, setMemberRole] = useState("member");
  const [memberEmail, setMemberEmail] = useState("");
  const [memberPhone, setMemberPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orgs/${orgId}/committee/propose`, {
        action, member_name: memberName, member_role: memberRole,
        member_email: memberEmail || null, member_phone: memberPhone,
      });
      toast.success("Proposal created — OTPs sent to approvers");
      try {
        const me = await api.get(`/orgs/${orgId}/committee/proposals/${data.id}/otp-for-dev`);
        if (me.data?.otp) {
          toast.info(`Your OTP (dev): ${me.data.otp}`, { duration: 12000 });
        }
      } catch (err) {
        console.warn("[ProposeDialog] approver-only OTP peek skipped:", err?.message);
      }
      onOpenChange(false);
      await onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not propose");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-md" data-testid="propose-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">Propose Committee Change</DialogTitle>
        <DialogDescription className="text-white/55 text-xs">
          3 OTP approvals required. Approvers get the code via email + SMS.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Action</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button onClick={() => setAction("add")}
                    className={`p-2.5 rounded-lg border text-sm uppercase tracking-[0.18em] ${
                      action === "add"
                        ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300"
                        : "bg-black/30 border-white/10 text-white/65"}`}
                    data-testid="action-add">Add</button>
            <button onClick={() => setAction("remove")}
                    className={`p-2.5 rounded-lg border text-sm uppercase tracking-[0.18em] ${
                      action === "remove"
                        ? "bg-red-500/15 border-red-500/50 text-red-300"
                        : "bg-black/30 border-white/10 text-white/65"}`}
                    data-testid="action-remove">Remove</button>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Member Name *</Label>
          <Input value={memberName} onChange={(e) => setMemberName(e.target.value)}
                 className="mt-2 h-10 bg-black/40 border-white/15" data-testid="propose-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Role</Label>
            <Input value={memberRole} onChange={(e) => setMemberRole(e.target.value)}
                   placeholder="secretary / treasurer / member"
                   className="mt-2 h-10 bg-black/40 border-white/15" data-testid="propose-role" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Phone</Label>
            <Input value={memberPhone} onChange={(e) => setMemberPhone(e.target.value)}
                   placeholder="+91 …"
                   className="mt-2 h-10 bg-black/40 border-white/15" data-testid="propose-phone" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Email</Label>
          <Input type="email" value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)}
                 className="mt-2 h-10 bg-black/40 border-white/15" data-testid="propose-email" />
        </div>
        <Button onClick={submit} disabled={!memberName || busy}
                className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
                data-testid="propose-submit">
          {busy ? "Sending OTPs…" : "Submit Proposal"}
        </Button>
      </div>
    </DialogContent>
  );
}

function ApproveDialog({ prop, orgId, onOpenChange, onApproved }) {
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/orgs/${orgId}/committee/proposals/${prop.id}/otp-for-dev`)
       .then(({ data }) => setOtp(data.otp || ""))
       .catch((err) => console.warn("[ApproveDialog] dev-otp fetch skipped:", err?.message));
  }, [prop.id, orgId]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/orgs/${orgId}/committee/proposals/${prop.id}/approve`,
        null, { params: { otp } }
      );
      if (data.status === "approved") {
        toast.success("Threshold met — committee change applied");
      } else {
        toast.success(`Approval recorded (${data.approvals}/${data.required})`);
      }
      onOpenChange();
      await onApproved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not approve");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-md" data-testid="approve-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">Submit OTP Approval</DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            {prop.action.toUpperCase()} · {prop.member_name} · {prop.approvals?.length || 0}/{prop.required_approvals} approvals
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-white/5 border border-white/10">
            <AlertTriangle size={14} className="text-amber-300 shrink-0 mt-0.5" />
            <div className="text-[11px] text-white/65">
              <strong>Dev mode</strong>: your OTP is auto-filled below. In production it arrives via email + SMS.
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">6-digit OTP</Label>
            <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                   maxLength={6} placeholder="000000"
                   className="mt-2 h-12 bg-black/40 border-white/15 font-mono text-2xl tracking-[0.4em] text-center"
                   data-testid="approve-otp-input" autoFocus />
          </div>
          <Button onClick={submit} disabled={otp.length !== 6 || busy}
                  className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
                  data-testid="approve-submit">
            {busy ? "Verifying…" : "Approve"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
